import React, { useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Collapsible } from '@opendoor/atoms/collapsible';
import { Text } from '@opendoor/atoms/text';
import * as Sentry from '@sentry/nextjs';
import { IconChevronDown } from '@tabler/icons-react';
import { ListingState } from '__generated__/athena/graphql';
import { getSellerDisclosureDownload } from 'components/marketplace/helpers/sellerDisclosures';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import LongTextNew from 'components/properties/shared/LongTextNew';
import { Divider } from 'components/shared/Divider';
import { formatCurrency } from 'helpers/formatters';

import { convertDwellingType } from './helpers';

type HomeDetailsProperty = Pick<
  MarketplaceProperty,
  | 'addressToken'
  | 'description'
  | 'dwellingType'
  | 'exteriorStories'
  | 'garageSpaces'
  | 'hasSellerDisclosures'
  | 'kitchenCountertop'
  | 'listingState'
  | 'masterBedroomLocation'
  | 'pricePerSqFt'
  | 'sqFtLot'
  | 'yearBuilt'
>;

interface HomeDetailsProps {
  property: HomeDetailsProperty;
}

const kitchenCountertopLabel = {
  CORIAN: 'Corian',
  FORMICA: 'Formica',
  GRANITE_SLAB: 'Granite slab',
  GRANITE_TILE: 'Granite tile',
  OTHER_TILE: 'Other',
  QUARTZ: 'Quartz',
  UNKNOWN: 'Other',
} satisfies Record<NonNullable<HomeDetailsProperty['kitchenCountertop']>, string>;

const convertMasterBedroomLocation = (
  masterBedroomLocation: HomeDetailsProperty['masterBedroomLocation'],
  exteriorStories: HomeDetailsProperty['exteriorStories'],
) => {
  switch (masterBedroomLocation) {
    case 'FIRST_FLOOR':
      if (exteriorStories && exteriorStories > 1) {
        return 'Downstairs bedroom';
      }
      return 'Main bedroom';
    case 'SECOND_FLOOR':
    case 'THIRD_FLOOR':
      return 'Upstairs bedroom';
    default:
      return null;
  }
};

export const HomeDetails: React.FC<HomeDetailsProps> = ({ property }: HomeDetailsProps) => {
  const [isDownloadingDisclosure, setIsDownloadingDisclosure] = useState(false);
  const [disclosureError, setDisclosureError] = useState<string | null>(null);
  const downloadSellerDisclosure = async () => {
    const disclosureWindow = window.open('', '_blank');
    if (disclosureWindow) {
      disclosureWindow.opener = null;
    }
    setIsDownloadingDisclosure(true);
    setDisclosureError(null);
    try {
      const result = await getSellerDisclosureDownload(property.addressToken);
      if (result.status === 'unavailable') {
        disclosureWindow?.close();
        setDisclosureError('This seller’s disclosure is no longer available.');
      } else if (disclosureWindow) {
        disclosureWindow.location.assign(result.url);
      } else {
        window.location.assign(result.url);
      }
    } catch (error) {
      disclosureWindow?.close();
      // GraphQL transport errors may include response data, so report only bounded metadata.
      Sentry.captureException(new Error('Seller disclosure download failed'), {
        tags: {
          feature: 'marketplace-property',
          operation: 'GetMarketplacePropertySellerDisclosures',
        },
        extra: {
          addressId: property.addressToken,
          upstreamErrorName: error instanceof Error ? error.name : typeof error,
        },
      });
      setDisclosureError('We couldn’t download the seller’s disclosure. Please try again.');
    } finally {
      setIsDownloadingDisclosure(false);
    }
  };
  const masterBedroomLocation = convertMasterBedroomLocation(
    property.masterBedroomLocation,
    property.exteriorStories,
  );

  const rows: Array<{ title: string; value: string | number | null }> = [
    {
      title: 'Home type',
      value: convertDwellingType(property.dwellingType),
    },
    {
      title: 'Lot size',
      value: property.sqFtLot ? `${property.sqFtLot.toLocaleString()} sqft lot` : null,
    },
    {
      title: 'Price per sqft',
      value: property.pricePerSqFt
        ? `${formatCurrency(property.pricePerSqFt, { inDollars: true })} / sqft`
        : null,
    },
    {
      title: 'Built in',
      value: property.yearBuilt,
    },
    {
      title: 'Kitchen',
      value: property.kitchenCountertop ? kitchenCountertopLabel[property.kitchenCountertop] : null,
    },
    {
      title: 'Garage',
      value: property.garageSpaces ? `${property.garageSpaces} car garage` : null,
    },
    {
      title: 'Master bedroom',
      value: masterBedroomLocation,
    },
  ];

  const filteredRows = rows.filter((row) => row.value !== null);
  const showPropertyDescription = property.listingState === ListingState.ON_THE_MARKET;

  return (
    <Collapsible.Root defaultOpen className="w-full">
      <Collapsible.Trigger className="group flex w-full items-center justify-between gap-2 py-6">
        <Text
          as="span"
          className="text-left text-label-xl text-od-content-primary lg:text-subheader-xs"
        >
          Home details
        </Text>
        <Box className="shrink-0 text-od-content-primary transition-transform duration-200 group-data-[panel-open]:rotate-180">
          <IconChevronDown size={20} />
        </Box>
      </Collapsible.Trigger>
      <Collapsible.Panel className="pb-4">
        <Col className="w-full" id="homeDetails">
          {showPropertyDescription && property.description && (
            <Col className="pb-6 max-md:pb-11">
              <LongTextNew content={property.description} limit={212} />
            </Col>
          )}
          {filteredRows.map((row, index) => (
            <Col key={row.title}>
              <Row className="w-full justify-between">
                <Text className="text-body-sm text-od-content-primary">{row.title}</Text>
                <Text className="text-label-sm text-od-content-primary">{row.value}</Text>
              </Row>
              {index !== filteredRows.length - 1 ? <Divider className="py-4" /> : null}
            </Col>
          ))}
          {property.hasSellerDisclosures !== false && (
            <Col className="py-6">
              <Button
                analyticsName="seller_disclosure_link"
                onClick={() => void downloadSellerDisclosure()}
                loading={isDownloadingDisclosure}
                size="lg"
                variant="secondary"
                aria-label="Download seller’s disclosure"
                className="w-full"
              >
                Download seller&rsquo;s disclosure
              </Button>
              {disclosureError && (
                <Text className="pt-3 text-body-sm text-od-content-error" role="alert">
                  {disclosureError}
                </Text>
              )}
            </Col>
          )}
        </Col>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
};
